import { Injectable } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { FavouritesConfig } from '@frontend/sports/common/client-config-data-access';
import { RegionConstant, SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { CountItem, LeagueItem } from '@frontend/sports/common/core/data-access/sport-model';
import { UserService } from '@frontend/sports/user/feature';
import { orderBy } from 'lodash-es';

import { BaseTreeItem, LeftNavType, TreeItem, TreeItemType } from '../../event-details-common/event-details-common.models';
import { LiveEventsNavigationStateService } from '../live-events-navigation/live-events-navigation-state.service';
import { LiveEventsNavigationTreeService } from '../live-events-navigation/live-events-navigation-tree.service';
import { NavState } from '../navigation-state/navigation-state';

@Injectable({ providedIn: 'root' })
export class NavigationMapperService {
    constructor(
        private stateService: LiveEventsNavigationStateService,
        private fixtureFactory: FixtureFactory,
        private userService: UserService,
        private favouritesConfig: FavouritesConfig,
        private treeService: LiveEventsNavigationTreeService,
    ) {}

    updateTreeState(tree: BaseTreeItem, state: NavState): BaseTreeItem {
        tree.children.forEach((sport) => {
            sport.expanded = state.expandedSports.has(sport.id);
            sport.children.forEach((league) => {
                league.expanded = !state.collapsedLeagues.has(league.id);
            });
        });

        return tree;
    }

    mapFixture(fixture: Fixture): EventModel {
        return this.fixtureFactory.create(fixture);
    }

    mapTree(sports: CountItem[], selectedLeague?: number, withLoading: boolean = true): BaseTreeItem {
        const sportItems: TreeItem[] = [];
        const favouriteChildren: TreeItem[] = [];

        if (this.userService.isAuthenticated && this.favouritesConfig.isLiveFavouritesEnabled) {
            const favouriteTab = this.treeService.getFavouriteTab();
            if (favouriteTab.children.length > 0) {
                favouriteChildren.push(favouriteTab);
            }
        }

        sports.forEach((sport) => {
            const leagues = this.mapLeagues(sport, selectedLeague, withLoading);
            sportItems.push(this.mapSport(sport, leagues, sport.counts.live));
        });

        return {
            children: [...favouriteChildren, ...sportItems],
        };
    }

    mapLeagues(sport: CountItem, selectedLeague: number | undefined, withLoading: boolean): TreeItem[] {
        let leagueRegions: {
            league: CountItem;
            region: CountItem;
        }[] = [];

        sport.children.forEach((region) => {
            leagueRegions = leagueRegions.concat(
                region.children.map((league) => ({
                    league,
                    region,
                })),
            );
        });

        return orderBy(leagueRegions, [(lr) => lr.league.order, (lr) => lr.region.order, (lr) => lr.league.name]).map((lr) => {
            const regionId = sport.id === SportConstant.Tennis ? RegionConstant.World : lr.region.id;

            return this.mapLeague(lr.league, regionId, sport.id, selectedLeague === lr.league.id, withLoading);
        });
    }

    private mapLeague(league: CountItem, regionId: number, sportId: number, selected: boolean, withLoading: boolean): TreeItem {
        const id = (<LeagueItem>league).siblings[0] || league.id;
        const collapsed = this.stateService.state.isLeagueCollapsed(id);

        return {
            id,
            sportId,
            name: league.name,
            type: TreeItemType.League,
            selected,
            expanded: !collapsed,
            loading: withLoading ? !collapsed : false,
            icon: 'country-icon c' + regionId,
            count: league.counts.live,
            class: 'league',
            children: [],
            events: [],
            collapsible: true,
            leftNavType: LeftNavType.Live,
            hidden: false,
            isVirtual: league.isVirtual,
            virtualCompetitionId: league.isVirtual ? league.id : undefined,
            imageProfile: (league as LeagueItem).imageProfile,
        };
    }

    private mapSport(sport: CountItem, children: TreeItem[], eventCount: number): TreeItem {
        return {
            id: sport.id,
            name: sport.name,
            type: TreeItemType.Sport,
            selected: false,
            expanded: this.stateService.state.isSportExpanded(sport.id),
            loading: false,
            icon: 'sport-icon sports-' + sport.id,
            count: eventCount,
            children,
            class: 'sport',
            events: [],
            toggleMarkets: !this.stateService.state.areMarketsCollapsed(sport.id),
            collapsible: true,
            leftNavType: LeftNavType.Live,
            hidden: false,
        };
    }
}
